import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  RGB,
  buildPalette,
  tintColor,
  shadeColor,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderPaymentSection,
  renderExtraFields,
  renderPixSection,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  detectDocumentKind,
  formatDocument,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

/**
 * "Botânico" template — visual suave para negócios de estética/bem-estar.
 * Cabeçalho centrado em fonte serifada (times), tons pastel derivados da cor da
 * marca e um ramo decorativo minimalista desenhado em vetor (arco pontilhado
 * com folhas de círculo) no canto superior. Tabela sem grade, só linhas
 * horizontais claras.
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  const { primaryColor, grayText, darkText } = buildPalette(data.brandColorRGB);

  // Tons derivados da cor da marca: profundo para títulos, pálidos para fundos.
  const deep = shadeColor(primaryColor, 0.35);
  const soft = tintColor(primaryColor, 0.55);
  const pale = tintColor(primaryColor, 0.82);
  const paler = tintColor(primaryColor, 0.93);

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // Faixa pastel no topo da 1ª página + ramo decorativo no canto.
  const bandHeight = 10;
  doc.setFillColor(...paler);
  doc.rect(0, 0, pageWidth, bandHeight, 'F');
  drawSprig(doc, pageWidth - 30, 16, soft, pale);

  let yPos = bandHeight + 12;

  // === 1. CABEÇALHO CENTRADO ===
  if (logoBase64) {
    try {
      const logoSize = 22;
      const format = getJsPdfImageFormat(logoBase64);
      doc.addImage(logoBase64, format, (pageWidth - logoSize) / 2, yPos, logoSize, logoSize);
      yPos += logoSize + 6;
    } catch {
      yPos += 2;
    }
  }

  if (data.company?.name) {
    doc.setFont('times', 'bold');
    doc.setFontSize(19);
    doc.setTextColor(...deep);
    doc.text(data.company.name, pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
  }

  const subLines: string[] = [];
  if (data.company?.cnpj) subLines.push(`CNPJ ${data.company.cnpj}`);
  if (data.company?.address) subLines.push(data.company.address);
  const contact: string[] = [];
  if (data.company?.phone) contact.push(data.company.phone);
  if (data.company?.email) contact.push(data.company.email);
  if (contact.length) subLines.push(contact.join('  ·  '));

  doc.setFont('times', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...grayText);
  subLines.forEach((line) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4.5;
  });

  // Divisor decorativo: linha fina com um losango central.
  yPos += 4;
  doc.setDrawColor(...soft);
  doc.setLineWidth(0.4);
  doc.line(pageWidth / 2 - 26, yPos, pageWidth / 2 - 4, yPos);
  doc.line(pageWidth / 2 + 4, yPos, pageWidth / 2 + 26, yPos);
  doc.setFillColor(...soft);
  doc.circle(pageWidth / 2, yPos, 1, 'F');
  yPos += 9;

  // Título do documento, serifado e espaçado.
  doc.setFont('times', 'bold');
  doc.setTextColor(...deep);
  // O charSpace de 1.5 ENTRA na conta da largura: sem informa-lo ao helper, a
  // medicao diz que coube e o desenho vaza (ver o comentario de fitHeaderTitle).
  const tituloCabecalho = fitHeaderTitle(doc, resolveDocumentTitle(data), contentWidth, 20, {
    charSpace: 1.5,
  });
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, pageWidth / 2, yPos, {
    align: 'center',
    charSpace: 1.5,
  });
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
  yPos += renderDocumentSubtitle(doc, data, {
    x: pageWidth / 2, y: yPos, maxWidth: contentWidth, color: grayText,
    align: 'center', font: 'times', style: 'italic',
  });
  yPos += 10;

  // === 2. DADOS DO CLIENTE (rótulo à esquerda, data à direita) ===
  const clientRows: Array<[string, string]> = [
    ['Cliente', data.client.name || 'Não informado'],
  ];
  if (data.client.phone) clientRows.push(['Telefone', data.client.phone]);
  if (data.client.document) {
    clientRows.push([
      detectDocumentKind(data.client.document) === 'cnpj' ? 'CNPJ' : 'CPF',
      formatDocument(data.client.document),
    ]);
  }
  if (data.client.address) {
    clientRows.push([
      'Endereço',
      doc.splitTextToSize(data.client.address, contentWidth - 30)[0],
    ]);
  }
  if (data.validityDays && data.quoteDate) {
    clientRows.push(['Válido até', calcValidityDate(data.quoteDate, data.validityDays)]);
  }
  if (data.reference) {
    clientRows.push([
      'Referência',
      doc.splitTextToSize(data.reference, contentWidth - 30)[0],
    ]);
  }

  const clientTop = yPos;
  doc.setFontSize(9.5);
  clientRows.forEach(([label, value]) => {
    doc.setFont('times', 'bold');
    doc.setTextColor(...grayText);
    doc.text(label, margin, yPos);
    doc.setFont('times', 'normal');
    doc.setTextColor(...darkText);
    doc.text(value, margin + 26, yPos);
    yPos += 5.2;
  });

  doc.setFont('times', 'bold');
  doc.setTextColor(...grayText);
  doc.text('Emitido em', pageWidth - margin - 40, clientTop);
  doc.setFont('times', 'normal');
  doc.setTextColor(...darkText);
  doc.text(formatQuoteDate(data.quoteDate), pageWidth - margin, clientTop, { align: 'right' });

  yPos += 4;

  // === 3. TABELA DE ITENS (sem grade; cabeçalho pastel, linhas claras) ===
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);
  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 15, halign: 'center' as const },
        2: { cellWidth: 13, halign: 'center' as const },
        3: { cellWidth: 27, halign: 'right' as const },
        4: { cellWidth: 20, halign: 'center' as const },
        5: { cellWidth: 29, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 17, halign: 'center' as const },
        2: { cellWidth: 13, halign: 'center' as const },
        3: { cellWidth: 31, halign: 'right' as const },
        4: { cellWidth: 31, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'plain',
    styles: { font: 'times' },
    headStyles: {
      fillColor: pale,
      textColor: deep,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      cellPadding: 3.5,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      lineColor: pale,
      lineWidth: { bottom: 0.3 },
    },
    columnStyles,
    margin: { left: margin, right: margin },
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 5;

  // === 4. TOTAL ===
  const totals = pdfQuoteTotals(data);
  const totalBoxWidth = 74;
  yPos = renderTotalsBreakdown(doc, totals, yPos, {
    margin,
    pageWidth,
    grayText,
    width: totalBoxWidth,
  });

  const totalBoxX = pageWidth - margin - totalBoxWidth;
  doc.setFillColor(...pale);
  doc.roundedRect(totalBoxX, yPos, totalBoxWidth, 13, 2, 2, 'F');
  doc.setFont('times', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...deep);
  doc.text('Total', totalBoxX + 5, yPos + 8.5);
  doc.setFontSize(13);
  doc.text(formatCurrency(totals.total), pageWidth - margin - 5, yPos + 8.5, { align: 'right' });
  yPos += 13 + 8;

  // === 5. OBSERVAÇÕES (parágrafos em itálico, sem caixa) ===
  if (data.warranty || data.notes || (data.validityDays && data.quoteDate)) {
    doc.setFont('times', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...deep);
    doc.text('Observações', margin, yPos);
    yPos += 5;

    doc.setFont('times', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...grayText);
    if (data.validityDays && data.quoteDate) {
      doc.text(
        `Proposta válida até ${calcValidityDate(data.quoteDate, data.validityDays)}.`,
        margin,
        yPos,
      );
      yPos += 4.5;
    }
    if (data.warranty) {
      const lines = doc.splitTextToSize(`Garantia: ${data.warranty}`, contentWidth);
      doc.text(lines, margin, yPos);
      yPos += 4.2 * lines.length + 1;
    }
    if (data.notes) {
      const lines = doc.splitTextToSize(data.notes, contentWidth);
      doc.text(lines, margin, yPos);
      yPos += 4.2 * lines.length + 1;
    }
    yPos += 4;
  }

  // === 6. DIAGNÓSTICO / PAGAMENTO / PIX (seções compartilhadas; o retorno
  // avança o yPos — jsPDF não tem layout, armadilha #13) ===
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });
  yPos = renderPaymentSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = await renderPixSection(doc, data, yPos, {
    primaryColor,
    grayText,
    darkText,
    margin,
    pageWidth,
    pageHeight,
  });

  // === 7. TERMOS ===
  if (data.termsConditions) {
    const lines = doc.splitTextToSize(data.termsConditions, contentWidth);
    if (yPos + lines.length * 3.8 + 12 > pageHeight - 30) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFont('times', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...deep);
    doc.text('Termos e condições', margin, yPos);
    yPos += 5;
    doc.setFont('times', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    doc.text(lines, margin, yPos);
    yPos += 3.8 * lines.length + 6;
  }

  // === 8. FOTOS ===
  yPos = await renderPhotosSection(doc, data, yPos, {
    darkText,
    grayText,
    grayLight: pale,
    margin,
    pageWidth,
    pageHeight,
  });

  // === 9. ASSINATURAS ===
  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  // Faixa pastel no rodapé de TODAS as páginas, desenhada antes do texto do
  // rodapé (que vem por cima). Puramente decorativa, abaixo da linha de
  // assinatura (pageHeight - 26), então nunca cobre conteúdo.
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(...paler);
    doc.rect(0, pageHeight - 4, pageWidth, 4, 'F');
  }

  // === 10. RODAPÉ ===
  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin,
    pageWidth,
    pageHeight,
  });
}

/**
 * Ramo decorativo minimalista: um caule em arco com "folhas" de círculo em
 * tamanhos decrescentes. Vetor puro (nenhuma imagem embutida) e deliberadamente
 * abstrato — é um aceno ao estilo botânico, não uma ilustração.
 */
function drawSprig(doc: jsPDF, x: number, y: number, stem: RGB, leaf: RGB): void {
  doc.setDrawColor(...stem);
  doc.setLineWidth(0.5);
  // Caule: arco suave subindo para a direita (bezier relativa ao ponto inicial).
  doc.lines(
    [
      [6, -4, 12, -6, 18, -6],
    ],
    x, y, [1, 1], 'S', false,
  );

  const leaves: Array<[number, number, number]> = [
    [3, -3.6, 2.6],
    [8, -5.2, 2.2],
    [13, -6.2, 1.8],
    [17.5, -6.6, 1.4],
  ];
  doc.setFillColor(...leaf);
  for (const [dx, dy, r] of leaves) {
    doc.circle(x + dx, y + dy, r, 'F');
  }
  doc.setFillColor(...stem);
  doc.circle(x, y, 1.1, 'F');
}
